/*  MUI  */
import { Add } from '@mui/icons-material';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Grid,
    TableCell,
    Typography
} from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { ManagedTable } from 'application/Shared/Components/Table';
import { FontSizes } from 'application/Shared/Constants';
import { Notify } from 'application/Shared/Helpers';
import { Modal, ModalBody, ModalFooter } from 'application/Shared/Modals';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  Objects  */
import {
    Application,
    ApplicationCategoryAndSubCategory
} from 'objects/Application';

/*  NPM  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';

const headers = [
    {
        align: 'right',
        field: null,
        renderHeader: <strong>Actions</strong>,
        width: '4%'
    },
    {
        align: 'left',
        field: 'name',
        renderHeader: <strong>Application</strong>,
        width: '48%'
    },
    {
        align: 'left',
        field: 'category',
        renderHeader: <strong>Category</strong>,
        width: '48%'
    }
];

const searchFields = ['name', 'description', 'category', 'baseName'];

class BulkEditModal extends Component {
    constructor(props) {
        super(props);

        this.state = {
            saving: false
        };
    }

    setAsPersonal = async () => {
        const { applications, onSave, personalId } = this.props;
        const applicationKeys = Object.keys(applications);

        this.setState({ saving: true }, async () => {
            Notify.i(`Updating ${applicationKeys.length} Applications`);
            debug.dev('Setting category for applications: ', applications);

            Object.entries(applications).forEach(async ([id, application]) => {
                try {
                    await axios.put(`/application/${id}`, {
                        category: personalId
                    });
                } catch (e) {
                    debug.e('Unable to set Category', e, application);
                    Notify.e(
                        `Unable to Update Application:
                        ${application.name}`
                    );
                }
            });

            Notify.s('Success!');
            this.setState({ saving: false });
            onSave();
        });
    };

    disableApplications = async () => {
        const { applications, onSave } = this.props;
        const applicationKeys = Object.keys(applications);

        this.setState({ saving: true }, async () => {
            Notify.i(`Disabling ${applicationKeys.length} Applications`);
            debug.dev('Disabling applications: ', applications);

            Object.entries(applications).forEach(async ([id, application]) => {
                try {
                    await axios.put(`/application/${id}`, {
                        disabled: true
                    });
                } catch (e) {
                    const err = `Unable to Disable Application ${application.name}`;
                    debug.e(err, e);
                    Notify.e(err);
                }
            });

            Notify.s('Success!');
            this.setState({ saving: false });
            onSave();
        });
    };

    mergeWith = (row) => {
        this.setState({ mergeApplication: row, saving: true }, async () => {
            const { mergeApplication } = this.state;
            const { applications, onSave } = this.props;

            const applicationKeys = Object.keys(applications);

            Notify.i(`Merging ${applicationKeys.length} Applications`);

            debug.dev(
                `Merging the following applications into APP-${mergeApplication.id}: `,
                applications
            );

            Object.entries(applications).forEach(async ([id, application]) => {
                if (mergeApplication.id !== application.id) {
                    try {
                        await axios.post('/application/merge', {
                            fromId: id,
                            toId: mergeApplication.id
                        });
                    } catch (err) {
                        Notify.error(
                            `Issue Merging Application: ${application.name}`
                        );
                    }
                }
            });
            Notify.success('Success!');
            this.setState({ saving: false });
            onSave();
        });
    };

    renderTable = () => {
        const { categorized } = this.props;

        return (
            <ManagedTable
                headers={headers}
                renderBody={(row) => (
                    <Application data={row}>
                        <TableCell align="right">
                            <Box
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                    this.mergeWith(row);
                                }}
                            >
                                <Add sx={{ color: 'button.primary' }} />
                            </Box>
                        </TableCell>
                        <TableCell>
                            <ApplicationNameLinkCell />
                            <small>{row.baseName}</small>
                        </TableCell>
                        <TableCell>
                            <ApplicationCategoryAndSubCategory size="normal" />
                        </TableCell>
                    </Application>
                )}
                renderBodyKey="id"
                renderFooter
                rowHeight={70}
                rows={categorized}
                rowsPerPage={100}
                searchEnabled
                searchFields={searchFields}
                sortDirection="ASC"
                sortFieldType="string"
                sortKey="name"
            />
        );
    };

    renderSetCategory = () => {
        const { saving } = this.state;

        return (
            <Grid
                item
                xs={12}
                style={{
                    borderBottom: 'solid',
                    paddingBottom: '40px',
                    textAlign: 'center'
                }}
            >
                <Button
                    onClick={() => this.setAsPersonal()}
                    color="primary"
                    variant="contained"
                    style={{
                        fontSize: FontSizes[3],
                        height: '250px',
                        marginRight: '4px',
                        width: '400px'
                    }}
                    disabled={saving}
                >
                    SET AS
                    <br />
                    PERSONAL / OTHER
                </Button>
            </Grid>
        );
    };

    render() {
        const { t, closeModal, applications, open } = this.props;
        const { saving } = this.state;

        if (!open) {
            return null;
        }

        const applicationKeys = Object.keys(applications);
        const numApplications = applicationKeys.length;

        return (
            <Modal
                open
                onClose={(_e, reason) => {
                    if (reason === 'backdropClick') {
                        closeModal();
                    }
                }}
                fullWidth
                scroll="paper"
                maxWidth="xl"
                title={
                    <Alert severity="warning" icon={false}>
                        <AlertTitle>
                            <Box display="flex" alignItems="center">
                                <span style={{ textTransform: 'uppercase' }}>
                                    {t('Common.bulkEditWithCount', {
                                        count: numApplications,
                                        name: applications[applicationKeys[0]]
                                            .name
                                    })}
                                    {` ( `} &nbsp;
                                </span>
                                {applications && (
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        data-testid="globalAdminAppModalBulkEditTitle"
                                    >
                                        {applicationKeys.map((id) => (
                                            <Box
                                                as="span"
                                                display="flex"
                                                key={id}
                                                data-trackid="globalAdminAppsModalsBulkEditAppMap"
                                                alignItems="center"
                                            >
                                                <Application
                                                    data={applications[id]}
                                                >
                                                    <ApplicationNameLinkCell nameClassname="bold" />
                                                </Application>
                                                &nbsp;&nbsp;
                                            </Box>
                                        ))}
                                        &nbsp;)
                                    </Box>
                                )}
                            </Box>
                        </AlertTitle>
                    </Alert>
                }
            >
                <ModalBody dividers>
                    <Grid container spacing={3}>
                        <Grid item xs={8} style={{ borderRight: 'solid' }}>
                            <Typography variant="h5">
                                Merge Applications Into:
                            </Typography>
                            {this.renderTable()}
                        </Grid>
                        <Grid item xs={4}>
                            {this.renderSetCategory()}
                            <Grid
                                item
                                xs={12}
                                style={{
                                    paddingTop: 40,
                                    textAlign: 'center'
                                }}
                            >
                                <Button
                                    onClick={() => this.disableApplications()}
                                    color="primary"
                                    variant="outlined"
                                    style={{
                                        fontSize: FontSizes[3],
                                        height: '250px',
                                        marginRight: '3px',
                                        width: '400px'
                                    }}
                                    disabled={saving}
                                >
                                    DISABLE
                                    <br />
                                    APPLICATIONS
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </ModalBody>
                <ModalFooter>
                    <Button
                        size="large"
                        disabled={saving}
                        color="primary"
                        variant="outlined"
                        onClick={() => closeModal(false)}
                    >
                        {t('Common.close')}
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }
}
export default withTranslation()(BulkEditModal);
